import { useEffect, useMemo } from 'react';
import { loadSavedCalculation } from '../database/savedCalculations';
import { parseURLFormData, type ShareableFormData } from '../urlUtils';
import { evaluateAppVersion, formatVersionWarning, type AppVersionPolicy } from '../versionPolicy';

// Short links from CalcPad use ?calcId=<id> and should bypass long-form URL parsing.
export function useCalcId(): number | null {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('calcId');
    return id ? parseInt(id, 10) : null;
  }, []);
}

export function useUrlFormValues(calcId: number | null): ShareableFormData {
  return useMemo(() => {
    // If calcId is present, the page will restore from IndexedDB instead.
    if (calcId) {
      return {};
    }

    return parseURLFormData() || {};
  }, [calcId]);
}

export function mergeInitialValues<T extends Record<string, unknown>>(defaultValues: T, urlFormValues: ShareableFormData): T {
  // App defaults stay local to each page; this helper only applies share-link overrides.
  return {
    ...defaultValues,
    ...urlFormValues
  } as T;
}

interface SavedCalculationLoaderOptions {
  appName: string;
  versionPolicy: AppVersionPolicy;
  blockRequiredVersionLoad?: boolean;
}

export function useSavedCalculationLoader<TInput, TResponse>(calcId: number | null, reset: (values: TInput) => void, setAnalysisResult: (result: TResponse | null) => void, options?: SavedCalculationLoaderOptions): void {
  useEffect(() => {
    // Pages opt into the same restore flow: load saved inputs/results by calcId,
    // reset the form, then repopulate the last analysis output if it exists.
    if (!calcId) return;

    loadSavedCalculation<TInput, TResponse>(calcId)
      .then(savedData => {
        if (!savedData) return;

        if (options?.versionPolicy) {
          const evaluation = evaluateAppVersion(savedData.metadata?.version, options.versionPolicy);
          if (evaluation.severity !== 'none') {
            window.alert(formatVersionWarning(savedData.appName ?? options.appName, evaluation));
          }
          if (evaluation.severity === 'required' && options.blockRequiredVersionLoad) {
            return;
          }
        }

        reset(savedData.inputs);
        setAnalysisResult(savedData.results ?? null);
      })
      .catch(err => {
        console.error('Failed to load from IndexedDB:', err);
      });
  }, [calcId, options?.appName, options?.blockRequiredVersionLoad, options?.versionPolicy, reset, setAnalysisResult]);
}
